import './history.css';

import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { allUserConfigsState } from '../../recoil/atoms/allUserConfigsState';
import { teamsState } from '../../recoil/atoms/teamsState';

import { translate } from '../../../common/language/translations';
import { Activity } from '../../types/activity';
import { PlayerReport } from '../../types/report';
import { getShortUserName, getDisplayTeamName } from '../../utils/nameUtils';
import { dateStringToTextFormat } from '../../utils/dateUtils';
import { shouldShowDateForHistoryEntry } from '../../utils/historyUtils';
import { getReportIcon } from '../../utils/iconUtils';


interface PlayerHistoryProps {
  history: (Activity | PlayerReport)[];
}


export const PlayerHistory: React.FC<PlayerHistoryProps> = ({ history }) => {

  const userConfig = useRecoilValue(userConfigState);
  const allUsersWithSameTenant = useRecoilValue(allUserConfigsState);
  const teams = useRecoilValue(teamsState);

  const [showHistory, setShowHistory] = useState(false);


  const getHistoryInfoText = (action: Activity | PlayerReport, isCurrentUser: boolean) => {
    if (!userConfig || !teams) return '';

    const userName = isCurrentUser
      ? translate('you', userConfig.language)
      : allUsersWithSameTenant[action.userEmail]
        ? getShortUserName(allUsersWithSameTenant[action.userEmail].name)
        : translate('unknownUser', userConfig.language);


    if ('reportType' in action) {
      const actionText = action.reportType === 'Attachment'
        ? translate('<someone>uploadedAnAttachment', userConfig.language)
        : translate('<someone>leftA<reportType>', userConfig.language) + ' ' + translate(action.reportType, userConfig.language, true);

      return userName + ' ' + actionText;
    }

    const teamName = getDisplayTeamName(action.teamId, teams, userConfig?.language, action.teamName);

    if (action.actionType === 'playerAdded') {
      return userName + ' ' + translate('<someone>addedThePlayerTo<team>', userConfig.language) + ' ' + teamName;
    }

    if (action.actionType === 'playerRemoved') {
      return userName + ' ' + translate('<someone>removedThePlayerFrom<team>', userConfig.language) + ' ' + teamName;
    }

    return action.actionType;
  };


  const scrollToBottom = () => {
    const commentField = document.querySelector('.player-history-action-section');
    if (commentField) {
      commentField.scrollTop = commentField.scrollHeight;
    }
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHistory(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    scrollToBottom();
  }, []);


  return (
    <div className='history-container player-history-container'>

      <div className={'history-action-section player-history-action-section' + (showHistory ? ' history-action-section-show' : '')}>
        {history.map((action, index) => {

          const isCurrentUser = userConfig !== null && action.userEmail === userConfig.email;
          const isReport = 'reportType' in action;

          return (
            <div
              key={index}
              className='history-action player-history-action'
              style={{ marginTop: index === 0 ? 0 : undefined }}
            >

              {shouldShowDateForHistoryEntry(index, history) && (
                <div className='history-date-row player-history-date-row'>

                  {isCurrentUser && <div className='history-date-large-divider' />}

                  <div className={'history-date-large' + (isCurrentUser ? ' history-date-current-user' : '')}>
                    {dateStringToTextFormat(action.date.split('T')[0])}
                  </div>

                  {!isCurrentUser && <div className='history-date-large-divider' />}

                </div>
              )}

              <div
                className={
                  'player-history-info-text'
                  + (isCurrentUser ? ' player-history-info-text-current-user' : '')
                  + (isReport ? (isCurrentUser ? ' player-history-report-info-current-user' : ' player-history-report-info-other-user') : '')
                }
              >
                {getHistoryInfoText(action, isCurrentUser)}

                {isReport && (
                  <div
                    className={
                      'player-history-report-icon' +
                      (isCurrentUser ? ' player-history-report-icon-current-user' : ' player-history-report-icon-other-user')
                    }
                  >
                    {getReportIcon(action.reportType, 21, '#ffffffbb')}
                  </div>
                )}
              </div>

            </div>
          );
        })}
      </div>

    </div>
  );
};
